import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar2 from '@/component/navbar2';
import Footer from '@/components/BrandFooter';
import { BRAND } from '@/brand';
import '../styles/publicPages.css';

// Decorative glow — faint gold on corporate, faint blue on consulting.
const IS_CORP = BRAND === 'corporate';
const GLOW = IS_CORP ? 'rgba(255,213,79,.14)' : 'rgba(25,118,210,.10)';

type FaqItem = {
  q: string;
  a: React.ReactNode;
};

type Category = {
  label: string;
  items: FaqItem[];
};

const CONSULTING_CATEGORIES: Category[] = [
  {
    label: 'Getting Started',
    items: [
      {
        q: 'What is Conncct?',
        a: 'Conncct is an all-in-one platform that helps startups and SMEs prepare for fundraising, manage their data rooms, and access expert consulting across finance, legal, and strategy — all in one place.',
      },
      {
        q: 'Who is Conncct designed for?',
        a: 'Conncct is made for founders, entrepreneurs, and small business owners who want to raise capital efficiently, stay compliant, and scale strategically.',
      },
      {
        q: 'Do I need to be an established business to use Conncct?',
        a: "No, you don't. Conncct works with both early-stage startups and growing SMEs. Whether you're raising your first seed round or scaling for Series A, our consultants can help you get investor-ready.",
      },
      {
        q: 'What information do you need from me to get started?',
        a: 'We just need your company name, business stage, and funding goals. Once you sign up, our onboarding form will guide you through creating your readiness profile and setting up your data room.',
      },
    ],
  },
  {
    label: 'Services',
    items: [
      {
        q: 'What is Conncct Consulting?',
        a: 'Conncct Consulting gives you access to experienced CFOs, legal advisors, and business strategists who tailor solutions to your growth goals — from refining your business model to improving your investor readiness.',
      },
      {
        q: 'Why should I use Conncct to raise capital?',
        a: 'Conncct brings everything investors want under one roof — your pitch deck, valuation, KPIs, and financials — while connecting you with verified investors and advisors.',
      },
      {
        q: 'Can Conncct help me with my business plan and valuation?',
        a: 'Absolutely. Our team helps you build a detailed business plan, financial forecast, and valuation model aligned with investor expectations in your industry.',
      },
      {
        q: 'How does the Investor Readiness Score work?',
        a: (
          <>
            <p>Our Investor Readiness Score benchmarks how fundable you are today across the factors investors actually weigh:</p>
            <ul>
              <li>A 14-factor diagnostic across traction, team, financials and governance</li>
              <li>A clear score with the specific gaps holding your raise back</li>
              <li>A prioritised action plan to close those gaps before you pitch</li>
              <li>A data room and financial model built to survive due diligence</li>
            </ul>
          </>
        ),
      },
    ],
  },
  {
    label: 'Data & Security',
    items: [
      {
        q: 'Is my data safe on Conncct?',
        a: 'Yes. All your business and investor data is encrypted and stored in your secure data room, accessible only to you and approved partners. We use bank-grade encryption and never sell your data.',
      },
      {
        q: 'Who can access the documents in my data room?',
        a: 'Only you and the partners or investors you explicitly grant access to can view your data room. You control permissions and can revoke access at any time.',
      },
    ],
  },
  {
    label: 'Partner Program',
    items: [
      {
        q: 'What is the Conncct Partner Program?',
        a: (
          <>
            <p>The Conncct Partner Program enables professionals — including consultants, accountants, accelerators, and advisors — to refer startups and businesses to our platform. Partners earn commissions on each successful engagement.</p>
            <p>Conncct simplifies access to:</p>
            <ul>
              <li>Business setup and compliance advisory</li>
              <li>Financial planning and investor readiness consulting</li>
              <li>Digital transformation and marketing services (via Tech-X.co)</li>
            </ul>
          </>
        ),
      },
      {
        q: 'What are the ways to partner with Conncct?',
        a: (
          <>
            <p>You can partner with Conncct in two main ways:</p>
            <ul>
              <li><strong>Referral Partner</strong> — Introduce clients to Conncct and earn a commission per successful signup.</li>
              <li><strong>Consulting Partner (Coming Soon)</strong> — Wholesale pricing, co-branding, and access to client dashboards for white-label consulting.</li>
            </ul>
            <p style={{ marginTop: 10 }}>Enterprise and accelerator partners can request custom collaboration packages based on volume and market reach.</p>
          </>
        ),
      },
      {
        q: 'What kind of clients can I refer?',
        a: (
          <>
            <p>Our ideal referrals include:</p>
            <ul>
              <li>Startups and SMEs seeking consulting or fundraising support</li>
              <li>Founders preparing investor materials or financial plans</li>
              <li>Businesses pursuing digital or operational transformation</li>
            </ul>
          </>
        ),
      },
      {
        q: 'What rewards do partners receive?',
        a: 'Earn up to 50% commissions and unlock exclusive partner benefits as the program grows — including co-marketing opportunities, event invitations, and early access to new tools.',
      },
      {
        q: 'How can I join the Partner Program?',
        a: (
          <>
            <p>
              Email us at{' '}
              <a href="mailto:referral@conncct.com" style={{ color: '#1976D2', fontWeight: 700 }}>
                referral@conncct.com
              </a>{' '}
              to get started. A member of our partnerships team will reach out within 2 business days.
            </p>
          </>
        ),
      },
    ],
  },
];

// Corporate (Run & Comply) FAQ — company setup, accounting, tax & compliance.
const CORPORATE_CATEGORIES: Category[] = [
  {
    label: 'Getting Started',
    items: [
      { q: 'What is Conncct Corporate?', a: 'Conncct Corporate is one senior team for setting up and running your UAE company — company formation, accounting & tax, PRO and visas, and ongoing compliance — in one place, for one fixed fee.' },
      { q: 'Who is Conncct Corporate for?', a: 'Founders and SMEs setting up in the UAE, and established companies who want their bookkeeping, VAT, Corporate Tax and licence renewals handled by one accountable team instead of four separate providers.' },
      { q: 'Do I need an existing company to start?', a: "No. We help you incorporate a new mainland or free-zone company from scratch, or take over the accounting and compliance for a company you already operate." },
      { q: 'What do you need from me to get started?', a: 'Just your company name (or the activity you want to license) and your emirate. From there our onboarding walks you through the licence, visas and the documents we file on your behalf.' },
    ],
  },
  {
    label: 'Services',
    items: [
      { q: 'What accounting and tax services do you offer?', a: (
        <>
          <p>Our accounting &amp; tax service covers the full UAE compliance lifecycle:</p>
          <ul>
            <li>Bookkeeping and bank reconciliation</li>
            <li>VAT registration and quarterly returns filed with the FTA</li>
            <li>UAE Corporate Tax (9% on profits over AED 375,000), registered and filed</li>
            <li>Payroll processing and audit-ready financial statements</li>
          </ul>
        </>
      ) },
      { q: 'Can you set up my company and visas?', a: 'Yes — company formation across mainland and every major free zone, plus PRO services, establishment cards, residence visas and bank-account introductions, all managed by one team.' },
      { q: 'How do you keep me compliant?', a: 'We put every obligation — licence renewal, VAT and Corporate Tax deadlines, and visa expiries — on one managed compliance calendar and file to the day, so nothing is ever missed.' },
      { q: 'How does pricing work?', a: 'Published plans and clear à-la-carte fees. Government fees are always charged at cost — never marked up — and you see your exact price before you pay anything.' },
    ],
  },
  {
    label: 'Data & Security',
    items: [
      { q: 'Is my data safe on Conncct?', a: 'Yes. Your company and financial documents are encrypted and stored in your secure data room, accessible only to you and your approved Conncct team. We use bank-grade encryption and never sell your data.' },
      { q: 'Who can access my documents?', a: 'Only you and the team members you explicitly grant access to. You control permissions and can revoke access at any time.' },
    ],
  },
  {
    label: 'Partner Program',
    items: [
      { q: 'What is the Conncct Partner Program?', a: (
        <>
          <p>The Partner Program lets professionals — accountants, consultants, accelerators and advisors — refer businesses to Conncct and earn a commission on each successful engagement. It simplifies access to:</p>
          <ul>
            <li>Company setup and compliance advisory</li>
            <li>Accounting, VAT and Corporate Tax</li>
            <li>PRO, visas and ongoing back-office</li>
          </ul>
        </>
      ) },
      { q: 'What rewards do partners receive?', a: 'Earn competitive commissions plus partner benefits as the program grows — co-marketing, event invitations and early access to new tools.' },
      { q: 'How can I join the Partner Program?', a: (
        <>
          <p>Email us at{' '}<a href="mailto:referral@conncct.com" style={{ color: '#E8890B', fontWeight: 700 }}>referral@conncct.com</a>{' '}to get started. Our partnerships team will reach out within 2 business days.</p>
        </>
      ) },
    ],
  },
];

const categories: Category[] = IS_CORP ? CORPORATE_CATEGORIES : CONSULTING_CATEGORIES;

function FaqAccordion({ items }: { items: FaqItem[] }) {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <div>
      {items.map((item, i) => {
        const isOpen = openIdx === i;
        return (
          <div key={i} className={`pub-faq-item${isOpen ? ' open' : ''}`}>
            <button className="pub-faq-btn" onClick={() => setOpenIdx(isOpen ? null : i)}>
              <span>{item.q}</span>
              <span className="pub-faq-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5"  y1="12" x2="19" y2="12"/>
                </svg>
              </span>
            </button>
            <div className="pub-faq-body">
              <div className="pub-faq-body-inner">
                <div className="pub-faq-answer">{item.a}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function FaqPage() {
  return (
    <div className="pub-page">
      <div style={{ position: 'sticky', top: 0, zIndex: 50 }}>
        <Navbar2 bgColor="white" logoTo="/" />
      </div>

      {/* ── HERO ── */}
      <section className="pub-hero cream" style={{ textAlign: 'center', paddingBottom: 72 }}>
        <div className="pub-inner narrow">
          <span className="pub-eye" style={{ marginBottom: 22, display: 'inline-flex' }}>FAQ</span>
          <h1 className="pub-h1" style={{ color: '#17263A', marginBottom: 20 }}>
            Frequently Asked <span className="pub-hl">Questions</span>
          </h1>
          <p className="pub-lead" style={{ margin: '0 auto', textAlign: 'center' }}>
            Everything you need to know about Conncct. Can't find your answer?{' '}
            <Link to="/contact" style={{ color: '#1976D2', fontWeight: 700, textDecoration: 'none' }}>
              Contact us →
            </Link>
          </p>
        </div>
      </section>

      {/* ── FAQ CATEGORIES ── */}
      <section className="pub-sec white" style={{ paddingTop: 52 }}>
        <div className="pub-inner narrow">

          {categories.map((cat, ci) => (
            <div key={ci} className="pub-faq-cat">
              <p className="pub-faq-cat-head">{cat.label}</p>
              <FaqAccordion items={cat.items} />
            </div>
          ))}

          {/* CTA at bottom */}
          <div style={{
            marginTop: 64,
            padding: '44px 48px',
            background: '#17263A',
            borderRadius: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 24,
            flexWrap: 'wrap',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <div
              style={{
                position: 'absolute', top: -60, right: -60,
                width: 300, height: 240,
                background: `radial-gradient(ellipse, ${GLOW} 0%, transparent 70%)`,
                pointerEvents: 'none',
              }}
              aria-hidden="true"
            />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <p style={{ margin: 0, fontWeight: 800, fontSize: 20, color: '#fff', marginBottom: 6 }}>
                Still have questions?
              </p>
              <p style={{ margin: 0, fontSize: 15, color: 'rgba(206,219,242,.8)' }}>
                Our team is happy to help — reach out any time.
              </p>
            </div>
            <Link
              to="/contact"
              className="pub-btn gold"
              style={{ position: 'relative', zIndex: 1, flexShrink: 0 }}
            >
              Contact us →
            </Link>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
